/**
 * TargetSchemaContainerNode — single canvas node that owns the entire
 * target-schema deliverable: mapping lifecycle (status, generation,
 * approval) in the header, plus a per-table grid in the body.
 *
 * Replaces both the old `mapping_output` aggregator node and the loose
 * row of `target_table` nodes. Folding them collapses the N→1 edge fan
 * that made the pipeline canvas unreadable for schemas with many tables.
 *
 * Header (always visible): schema name · SCHEMA MAPPING subtitle · lifecycle
 * badge (pending / generating / mapped / approved / failed) · counter
 * "X/N mapped" · Open Mapping Editor / Change Schema actions · expand/
 * collapse chevron.
 *
 * Body (expanded only): scrollable table list — name + status icon + health
 * dot. Click a row to open the inspector for that table.
 *
 * Lifecycle parity covers both flows:
 *   - Iterative mapping: counter ticks up as per-table schema_map ops complete;
 *     pulse on whenever any child is running; Open Editor activates once ≥1
 *     table is mapped.
 *   - Mapping engine: counter jumps from 0/N to N/N when batch flips to
 *     `mapped`; pulse follows `batchStatus === 'mapping'`.
 */
import React, { useState } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Box, Button, Chip, Link, Tooltip, Typography, alpha } from '@mui/material';
import {
  Check as CheckIcon,
  TableChart as MappingIcon,
  HourglassEmpty as PendingIcon,
  ErrorOutline as ErrorIcon,
  OpenInNew as OpenIcon,
  ExpandMore as ChevronDownIcon,
  ExpandLess as ChevronUpIcon,
  CheckCircle as SuccessIcon,
  Error as FailIcon,
} from '@mui/icons-material';

export interface TargetSchemaTableEntry {
  name: string;
  viewName: string;
  status: 'completed' | 'failed' | 'pending' | 'running' | 'awaiting_review';
  ddl?: string;
  error?: string;
  healthIssueCount?: number;
  healthSeverity?: 'error' | 'warning' | null;
}

export interface TargetSchemaContainerNodeData {
  /** Schema name (e.g. "cris135"). */
  title: string;
  /** Short lifecycle description ("Generating…", "24 tables mapped"). */
  description: string;
  /** Reactflow status. */
  status: 'pending' | 'running' | 'completed' | 'failed' | 'awaiting_review';
  /** Batch lifecycle status for finer-grained UI states. */
  batchStatus?: string;
  /** Workspace mapping mode — engine runs pulse on batch 'mapping'; iterative pulses only per-table activity. */
  mappingMode?: string;
  /** All target tables in this schema, with per-table status. */
  tables: TargetSchemaTableEntry[];
  /** Open the field-level mapping editor. */
  onOpenEditor?: () => void;
  /** Open schema picker (pending state). */
  onChangeSchema?: () => void;
  /** Click a specific table row → open inspector for that table. */
  onTableClick?: (tableName: string) => void;
  /** Default expanded? When undefined, collapsed when ≥6 tables. */
  defaultExpanded?: boolean;
}

// Body height cap before scrolling kicks in. The expanded list shows ALL
// tables — the scroll container handles overflow, no truncation chip.
const BODY_MAX_HEIGHT = 220;

const TargetSchemaContainerNode: React.FC<NodeProps<TargetSchemaContainerNodeData>> = ({
  data,
  selected,
  sourcePosition,
  targetPosition,
}) => {
  const targetHandlePos = targetPosition ?? Position.Top;
  const sourceHandlePos = sourcePosition ?? Position.Bottom;
  const {
    title,
    description,
    status,
    batchStatus,
    mappingMode,
    tables,
    onOpenEditor,
    onChangeSchema,
    onTableClick,
    defaultExpanded,
  } = data;

  const tableCount = tables.length;
  const completedCount = tables.filter((t) => t.status === 'completed').length;
  const failedCount = tables.filter((t) => t.status === 'failed').length;
  const runningCount = tables.filter((t) => t.status === 'running').length;

  const isApproved = batchStatus === 'approved';
  const isPending = batchStatus === 'ready_for_mapping' || (!batchStatus && tableCount === 0);
  const isGenerating =
    runningCount > 0 ||
    (batchStatus === 'mapping' && mappingMode !== 'iterative') ||
    (status === 'running' && mappingMode === 'iterative' && runningCount > 0);
  const isMapped =
    !isGenerating &&
    (batchStatus === 'mapped' || (tableCount > 0 && completedCount === tableCount));
  const isFailed = status === 'failed' || (failedCount > 0 && !isGenerating);

  const accentColor = isFailed
    ? '#dc2626'
    : failedCount > 0
      ? '#f59e0b'
      : '#059669';

  // Default to expanded so the per-table list is visible at a glance; user
  // can collapse via the chevron chip. Sticky to user choice once toggled.
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded ?? true);

  const statusIcon = isFailed ? (
    <ErrorIcon sx={{ fontSize: 14, color: '#fff' }} />
  ) : isGenerating ? (
    <PendingIcon sx={{ fontSize: 14, color: '#fff' }} />
  ) : isApproved || isMapped ? (
    <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
  ) : (
    <PendingIcon sx={{ fontSize: 14, color: '#fff' }} />
  );

  // Header summary line — favours live progress when generating, falls back to
  // the builder-supplied description otherwise.
  const summary = (() => {
    if (isPending) return description || 'Awaiting schema selection';
    if (tableCount === 0) return description;
    if (isGenerating) return `${completedCount}/${tableCount} mapped · generating…`;
    if (failedCount > 0) return `${completedCount}/${tableCount} mapped · ${failedCount} failed`;
    if (completedCount === tableCount) return `${tableCount} tables mapped`;
    return `${completedCount}/${tableCount} mapped`;
  })();

  const canOpenEditor = !!onOpenEditor && (completedCount > 0 || isMapped || isApproved);

  const visibleTables = expanded ? tables : [];

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
      }}
      sx={{
        width: 320,
        background: '#fff',
        borderRadius: 0.5,
        border: selected
          ? `2px solid ${accentColor}`
          : isPending
            ? `1.5px dashed ${alpha(accentColor, 0.4)}`
            : '1.5px solid #d1fae5',
        borderLeft: `4px solid ${accentColor}`,
        boxShadow: selected
          ? `0 0 0 3px ${alpha(accentColor, 0.15)}`
          : '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        ...(isGenerating && {
          animation: 'targetSchemaPulse 2s ease-in-out infinite',
          '@keyframes targetSchemaPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0px ${alpha(accentColor, 0.1)}` },
            '50%': { boxShadow: `0 0 0 4px ${alpha(accentColor, 0.15)}` },
          },
        }),
      }}
    >
      <Handle
        type="target"
        position={targetHandlePos}
        style={{
          background: accentColor,
          width: 8,
          height: 8,
          border: `2px solid ${alpha(accentColor, 0.3)}`,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: expanded ? '1px solid #e2e8f0' : 'none',
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: alpha(accentColor, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MappingIcon sx={{ fontSize: 16, color: accentColor }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{
              fontSize: 12,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              color: '#6b7280',
              fontVariant: 'small-caps',
              letterSpacing: 0.5,
            }}
          >
            SCHEMA MAPPING
          </Typography>
        </Box>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: accentColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            ...(isGenerating && {
              animation: 'targetSchemaBadgeSpin 1.5s linear infinite',
              '@keyframes targetSchemaBadgeSpin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }),
          }}
        >
          {statusIcon}
        </Box>
      </Box>

      {/* Summary + actions row */}
      <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              color: isGenerating ? accentColor : isFailed ? '#dc2626' : '#64748b',
              fontWeight: isGenerating ? 600 : 400,
              display: 'block',
              lineHeight: 1.3,
            }}
          >
            {summary}
          </Typography>
        </Box>
        {tableCount > 0 && (
          <Tooltip
            title={expanded ? 'Hide tables' : `Show ${tableCount} tables`}
            placement="top"
            arrow
            disableInteractive
          >
            <Chip
              label={tableCount}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              icon={
                expanded ? (
                  <ChevronUpIcon sx={{ fontSize: 14 }} />
                ) : (
                  <ChevronDownIcon sx={{ fontSize: 14 }} />
                )
              }
              sx={{
                fontSize: 10,
                height: 22,
                fontWeight: 700,
                bgcolor: alpha(accentColor, 0.12),
                color: accentColor,
                cursor: 'pointer',
                '& .MuiChip-icon': { color: accentColor, ml: 0.25, mr: -0.25 },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Actions row */}
      {(canOpenEditor || (isPending && onChangeSchema)) && (
        <Box sx={{ px: 1.5, pb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
          {canOpenEditor && (
            <Button
              size="small"
              variant="contained"
              startIcon={<OpenIcon sx={{ fontSize: '12px !important' }} />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onOpenEditor!();
              }}
              sx={{
                fontSize: 10,
                textTransform: 'none',
                bgcolor: accentColor,
                '&:hover': { bgcolor: isFailed ? '#b91c1c' : '#047857' },
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                minHeight: 0,
                lineHeight: 1.4,
                fontWeight: 700,
              }}
            >
              Open Mapping Editor
            </Button>
          )}
          {isPending && onChangeSchema && (
            <Link
              component="button"
              variant="caption"
              underline="hover"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onChangeSchema();
              }}
              sx={{
                fontSize: 10,
                color: '#7c3aed',
                cursor: 'pointer',
                fontWeight: 600,
                '&:hover': { color: '#6d28d9' },
              }}
            >
              Select Schema ▸
            </Link>
          )}
        </Box>
      )}

      {/* Table list (expanded only) — shows ALL tables; scrolls when long */}
      {expanded && tableCount > 0 && (
        <Box
          className="nowheel"
          onWheelCapture={(e) => e.stopPropagation()}
          sx={{
            px: 1.5,
            py: 0.75,
            borderTop: '1px solid #e2e8f0',
            maxHeight: BODY_MAX_HEIGHT,
            overflowY: 'auto',
          }}
        >
          {visibleTables.map((t) => {
            const StatusIconComponent =
              t.status === 'completed'
                ? SuccessIcon
                : t.status === 'failed'
                  ? FailIcon
                  : PendingIcon;
            const iconColor =
              t.status === 'completed'
                ? '#22c55e'
                : t.status === 'failed'
                  ? '#ef4444'
                  : t.status === 'running'
                    ? '#3b82f6'
                    : '#94a3b8';

            return (
              <Box
                key={t.name}
                onClick={(e) => {
                  e.stopPropagation();
                  onTableClick?.(t.name);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  py: 0.25,
                  cursor: 'pointer',
                  borderRadius: 0.5,
                  '&:hover': { bgcolor: alpha('#2563eb', 0.04) },
                }}
              >
                <StatusIconComponent
                  sx={{ fontSize: 12, color: iconColor, flexShrink: 0 }}
                />
                <Tooltip title={t.error || t.name} placement="top" arrow disableInteractive>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontFamily: 'monospace',
                      color: t.status === 'failed' ? '#ef4444' : '#334155',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}
                  >
                    {t.name}
                  </Typography>
                </Tooltip>
                {!!t.healthIssueCount && t.healthIssueCount > 0 && (
                  <Tooltip
                    title={`${t.healthIssueCount} issue${t.healthIssueCount !== 1 ? 's' : ''}`}
                    placement="top"
                    arrow
                    disableInteractive
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: t.healthSeverity === 'error' ? '#ef4444' : '#f59e0b',
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <Handle
        type="source"
        position={sourceHandlePos}
        style={{
          background: accentColor,
          width: 8,
          height: 8,
          border: `2px solid ${alpha(accentColor, 0.3)}`,
        }}
      />
    </Box>
  );
};

export default TargetSchemaContainerNode;
